import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getTransactionDetails } from "src/api/bi";
import {
  DOLLAR,
  TRANSACTION_QUALIFIER
} from "src/config/constants/genericConstants";
import { resetStore } from "src/store/actions";
import convertToCurrency from "src/utils/convertToCurrency";
import { formatToCurrency, trimZeros } from "src/utils/genericFunctions";
import { currencyToNumber } from "src/utils/overShortUtils";
import stringToNumber from "src/utils/stringToNumber";
import { filterQualifiers } from "../TransactionLogReport/slice/TransactionLogReportReducers";

interface TransactionDetailsTableData {
  department: string;
  description: string;
  itemId: string;
  price: string;
  quantity: string;
  taxable: string;
  transactionSeq: string;
  upc: string;
}

interface TransactionDetailsInterface {
  basketAOV: string;
  basketQuantity: string;
  basketRetail: string;
  contributionToRetail: string;
}

interface TransactionDetailsInitialState {
  data: TransactionDetailsInterface;
  products: TransactionDetailsTableData[];
  recordsCount: number;
  status: "idle" | "pending" | "error";
  error: string | null;
}

type TransactionProductTableType<T> = T & {
  isRowHighlighted?: string;
  tags?: string[];
};

type TransactionProductsType<T> = T & {
  tags?: string[];
  itemId: string;
  transactionSeq: number;
  price: number | string;
  quantity: string;
  department: string;
};

export const fetchTransactionDetails = createAsyncThunk(
  "/products/transactionDetails",
  (params: {
    storeId: string;
    transactionId: string;
    itemId: string;
    time: string;
    registerNumber: string;
    date: string;
  }) =>
    getTransactionDetails({
      storeId: params.storeId,
      transactionId: params.transactionId,
      time: params.time,
      registerNumber: params.registerNumber,
      date: params.date
    })
);

const initialState: TransactionDetailsInitialState = {
  data: {
    basketAOV: "$0",
    basketQuantity: "0",
    basketRetail: "$0",
    contributionToRetail: "0"
  },
  products: [],
  recordsCount: 0,
  status: "idle",
  error: null
};

const TransactionDetailsSlice = createSlice({
  name: "Transaction Details",
  initialState,
  reducers: {
    updateProducts(state: TransactionDetailsInitialState, action: any) {
      state.products = action.payload;
    },
    resetTransactionDetails() {
      return initialState;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchTransactionDetails.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        fetchTransactionDetails.fulfilled,
        (state, { payload, meta }) => {
          const { itemId } = meta.arg;
          payload.data = filterQualifiers(payload.data)?.[
            TRANSACTION_QUALIFIER.NORMAL_SALES_ITEM
          ];
          state.data = payload;
          let basketRetail = 0;
          let basketQuantity = 0;
          let highlightedRetail = 0;
          state.products = payload.data?.map(
            <T>(
              product: TransactionProductsType<T>,
              index: number
            ): TransactionProductTableType<T> => {
              if (trimZeros(product?.itemId) === trimZeros(itemId)) {
                product = {
                  ...product,
                  isRowHighlighted: true,
                  departmentName: product.department
                };
                highlightedRetail = product?.price as number;
              }
              product.price = formatToCurrency(stringToNumber(product.price));
              product.transactionSeq = index + 1;
              basketRetail += currencyToNumber(product.price);
              basketQuantity += Number(product.quantity);
              return product;
            }
          );
          state.data.basketRetail = convertToCurrency(DOLLAR, basketRetail);
          state.data.basketQuantity = String(basketQuantity);
          state.data.basketAOV = !isNaN(basketRetail / basketQuantity)
            ? convertToCurrency(DOLLAR, basketRetail / basketQuantity)
            : "$0";
          state.data.contributionToRetail = !isNaN(
            highlightedRetail / basketRetail
          )
            ? String(((highlightedRetail / basketRetail) * 100).toFixed(2))
            : "0";

          state.status = "idle";
        }
      )
      .addCase(fetchTransactionDetails.rejected, (state, _action) => {
        state.status = "error";
      });
  }
});

export const getTransactionProducts = ({
  transactionDetails
}: {
  transactionDetails: TransactionDetailsInitialState;
}) => {
  return transactionDetails.products;
};

export const getTransactionDetailsAPIStatus = ({
  transactionDetails
}: {
  transactionDetails: TransactionDetailsInitialState;
}) => {
  return transactionDetails.status;
};

export const getTransactionDetailData = ({
  transactionDetails
}: {
  transactionDetails: TransactionDetailsInitialState;
}) => {
  return {
    basketAOV: transactionDetails.data.basketAOV,
    basketQty: transactionDetails.data.basketQuantity,
    basketRetail: transactionDetails.data.basketRetail,
    contributionToRetail: transactionDetails.data.contributionToRetail
  };
};

export const { resetTransactionDetails, updateProducts } =
  TransactionDetailsSlice.actions;

export default TransactionDetailsSlice.reducer;
